import React from "react";

/**
 * MetricCard
 * ───────────
 * Displays a single KPI value with an optional period-over-period delta.
 * Rendered when structured_data.structured_data_type === "metric_card".
 *
 * Props
 * -----
 * data : metric_card payload from format_metric_card tool
 */
const MetricCard = ({ data }) => {
  if (!data) return null;

  const {
    value,
    label = "",
    unit = "",
    delta,
    delta_label = "",
    pct_change,
    direction,
    sources = [],
  } = data;

  const isUp = direction === "up";
  const isDown = direction === "down";

  const formatValue = (v) => {
    if (v === null || v === undefined) return "—";
    const num = parseFloat(v);
    if (isNaN(num)) return String(v);
    if (Math.abs(num) >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (Math.abs(num) >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const formatDelta = (v) => {
    const num = parseFloat(v);
    if (isNaN(num)) return "";
    const sign = num >= 0 ? "+" : "";
    if (Math.abs(num) >= 1_000_000) return `${sign}${(num / 1_000_000).toFixed(1)}M`;
    if (Math.abs(num) >= 1_000) return `${sign}${(num / 1_000).toFixed(1)}K`;
    return `${sign}${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="w-full max-w-xs rounded-2xl border border-gray-100 bg-white shadow-md px-5 py-4">
      {/* Label */}
      <p className="text-[0.78rem] font-bold text-gray-400 uppercase tracking-widest mb-1 truncate">
        {label}
      </p>

      {/* Value */}
      <div className="flex items-baseline gap-2">
        <span className="text-[2.2rem] font-extrabold text-gray-900 leading-none tracking-tight">
          {formatValue(value)}
        </span>
        {unit && (
          <span className="text-[0.9rem] font-semibold text-gray-400">{unit}</span>
        )}
      </div>

      {/* Delta */}
      {delta !== undefined && delta !== null && (
        <div className={`flex items-center gap-1.5 mt-2 text-[0.82rem] font-semibold
          ${isUp ? "text-emerald-600" : isDown ? "text-red-500" : "text-gray-500"}`}>
          {isUp && (
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          )}
          {isDown && (
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
          <span>{formatDelta(delta)}</span>
          {unit && <span className="text-gray-400">{unit}</span>}
          {pct_change !== null && pct_change !== undefined && (
            <span className="ml-1 text-gray-400 font-normal">
              ({pct_change > 0 ? "+" : ""}{pct_change}%)
            </span>
          )}
          {delta_label && (
            <span className="text-gray-400 font-normal">{delta_label}</span>
          )}
        </div>
      )}

      {/* Sources */}
      {sources && sources.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {sources.map((s, i) => (
            <span key={i} className="text-[0.65rem] font-bold text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MetricCard;
